import { useState, useEffect, useCallback, useRef } from 'react';
import { API_URL } from '../config/apiConfig';
import {
  EXPLORE_UNKNOWN_MINIGAME_BEYOND_BIRD_POP_COUNT,
  EXPLORE_UNKNOWN_MINIGAME_WOMBAFLEKS_WIN_PICK_VALUE,
  EXPLORE_UNKNOWN_MINIGAME_REWARD_MEMORY_CARD_COUNT,
  EXPLORE_UNKNOWN_MINIGAME_REWARD_MEMORY_MAX_ATTEMPTS,
  EXPLORE_UNKNOWN_MINIGAME_REWARD_MEMORY_WIN_PICK_VALUE,
  EXPLORE_UNKNOWN_MINIGAME_BALLOON_BOOTH_BALLOON_COUNT,
  EXPLORE_UNKNOWN_MINIGAME_BALLOON_BOOTH_COMPLETION_PICK_VALUE,
  EXPLORE_UNKNOWN_MINIGAME_THE_GAMBLE_WIN_PICK_VALUE,
  EXPLORE_UNKNOWN_MINIGAME_SPOTTING_OBJECTS_FACTION_COUNT,
  isExploreUnknownMinigameEnabledForSurface,
  isGlobalMissionOnExploreUnknownMinigameSurface,
} from '../config/exploreUnknownMinigames';

/**
 * @param {object} params
 * @param {number | null} params.activeRunId
 * @param {string} params.exploreSurface
 * @param {string | null} params.sequencePhase
 * @param {number | null} params.nextAvailableMissionIndex global mission index for the current map window
 * @param {() => Promise<string>} params.ensureValidToken
 * @param {(n: number) => void} params.onDarknessPercentUpdated
 * @param {(opts?: { winReward?: { id: string } | null }) => Promise<void>} [params.onRunStateSyncedAfterComplete]
 */
export function useExploreUnknownMinigameSurface({
  activeRunId,
  exploreSurface,
  sequencePhase,
  nextAvailableMissionIndex = null,
  ensureValidToken,
  onDarknessPercentUpdated,
  onRunStateSyncedAfterComplete,
}) {
  const [resolveBusy, setResolveBusy] = useState(false);
  const [resolveError, setResolveError] = useState(null);
  const [spawned, setSpawned] = useState(false);
  const [marker, setMarker] = useState(null);
  const [minigameId, setMinigameId] = useState(null);
  const [challenge, setChallenge] = useState(null);
  const [outcome, setOutcome] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [completeBusy, setCompleteBusy] = useState(false);
  const [completeError, setCompleteError] = useState(null);
  /** @type {null | 'won' | 'lost'} */
  const [completeOutcome, setCompleteOutcome] = useState(null);
  const [completeWinReward, setCompleteWinReward] = useState(null);
  const [completeWinRewards, setCompleteWinRewards] = useState(null);
  const [completeGambleResolution, setCompleteGambleResolution] = useState(null);
  const [devMinigameTestBusy, setDevMinigameTestBusy] = useState(false);
  const [devMinigameTestError, setDevMinigameTestError] = useState(null);
  const [activeMissionIndex, setActiveMissionIndex] = useState(null);
  const lastResolveKeyRef = useRef(null);
  const lastResolveRunIdRef = useRef(null);

  const applyMinigameSurfacePayload = useCallback((surfacePayload) => {
    if (!surfacePayload || typeof surfacePayload !== 'object') {
      throw new Error('Minigame: missing surface payload');
    }
    const nextMinigameId =
      typeof surfacePayload.minigameId === 'string' ? surfacePayload.minigameId : null;
    const nextChallenge =
      surfacePayload.challenge && typeof surfacePayload.challenge === 'object'
        ? surfacePayload.challenge
        : null;
    if (nextMinigameId === 'beyondBirdMemory' && surfacePayload.spawned === true) {
      if (
        !Array.isArray(nextChallenge?.sequence) ||
        nextChallenge.sequence.length !== EXPLORE_UNKNOWN_MINIGAME_BEYOND_BIRD_POP_COUNT
      ) {
        throw new Error(
          `Minigame challenge must have ${EXPLORE_UNKNOWN_MINIGAME_BEYOND_BIRD_POP_COUNT} bird pops (got ${Array.isArray(nextChallenge?.sequence) ? nextChallenge.sequence.length : 0}). Restart the game server.`
        );
      }
    }
    if (nextMinigameId === 'wombafleksCatch' && surfacePayload.spawned === true) {
      if (nextChallenge?.winPickValue !== EXPLORE_UNKNOWN_MINIGAME_WOMBAFLEKS_WIN_PICK_VALUE) {
        throw new Error(
          `Minigame challenge must have winPickValue ${EXPLORE_UNKNOWN_MINIGAME_WOMBAFLEKS_WIN_PICK_VALUE}. Restart the game server.`
        );
      }
    }
    if (nextMinigameId === 'rewardMemory' && surfacePayload.spawned === true) {
      if (
        !Array.isArray(nextChallenge?.cardRewardIds) ||
        nextChallenge.cardRewardIds.length !== EXPLORE_UNKNOWN_MINIGAME_REWARD_MEMORY_CARD_COUNT
      ) {
        throw new Error(
          `Minigame challenge must have ${EXPLORE_UNKNOWN_MINIGAME_REWARD_MEMORY_CARD_COUNT} cards (got ${Array.isArray(nextChallenge?.cardRewardIds) ? nextChallenge.cardRewardIds.length : 0}). Restart the game server.`
        );
      }
      if (nextChallenge?.winPickValue !== EXPLORE_UNKNOWN_MINIGAME_REWARD_MEMORY_WIN_PICK_VALUE) {
        throw new Error(
          `Minigame challenge must have winPickValue ${EXPLORE_UNKNOWN_MINIGAME_REWARD_MEMORY_WIN_PICK_VALUE}. Restart the game server.`
        );
      }
      if (nextChallenge?.maxAttempts !== EXPLORE_UNKNOWN_MINIGAME_REWARD_MEMORY_MAX_ATTEMPTS) {
        throw new Error(
          `Minigame challenge must have maxAttempts ${EXPLORE_UNKNOWN_MINIGAME_REWARD_MEMORY_MAX_ATTEMPTS} (got ${String(nextChallenge?.maxAttempts)}). Restart the game server.`
        );
      }
    }
    if (nextMinigameId === 'balloonBoothPop' && surfacePayload.spawned === true) {
      if (
        !Array.isArray(nextChallenge?.balloons) ||
        nextChallenge.balloons.length !== EXPLORE_UNKNOWN_MINIGAME_BALLOON_BOOTH_BALLOON_COUNT
      ) {
        throw new Error(
          `Minigame challenge must have ${EXPLORE_UNKNOWN_MINIGAME_BALLOON_BOOTH_BALLOON_COUNT} balloons (got ${Array.isArray(nextChallenge?.balloons) ? nextChallenge.balloons.length : 0}). Restart the game server.`
        );
      }
      if (nextChallenge?.completionPickValue !== EXPLORE_UNKNOWN_MINIGAME_BALLOON_BOOTH_COMPLETION_PICK_VALUE) {
        throw new Error(
          `Minigame challenge must have completionPickValue ${EXPLORE_UNKNOWN_MINIGAME_BALLOON_BOOTH_COMPLETION_PICK_VALUE}. Restart the game server.`
        );
      }
    }
    if (nextMinigameId === 'theGamble' && surfacePayload.spawned === true) {
      if (!Array.isArray(nextChallenge?.deck) || nextChallenge.deck.length < 1) {
        throw new Error('Minigame challenge must include a strategy deck. Restart the game server.');
      }
      if (nextChallenge?.winPickValue !== EXPLORE_UNKNOWN_MINIGAME_THE_GAMBLE_WIN_PICK_VALUE) {
        throw new Error(
          `Minigame challenge must have winPickValue ${EXPLORE_UNKNOWN_MINIGAME_THE_GAMBLE_WIN_PICK_VALUE}. Restart the game server.`
        );
      }
    }
    if (nextMinigameId === 'spottingObjects' && surfacePayload.spawned === true) {
      if (!Number.isInteger(nextChallenge?.orderId)) {
        throw new Error('Minigame challenge must include orderId. Restart the game server.');
      }
      if (Object.prototype.hasOwnProperty.call(nextChallenge, 'correctFactionIndex')) {
        throw new Error('Minigame challenge leaked server answer. Restart the game server.');
      }
    }
    if (nextMinigameId === 'autobahnLaneSimon' && surfacePayload.spawned === true) {
      if (!Array.isArray(nextChallenge?.pattern) || nextChallenge.pattern.length < 1) {
        throw new Error(
          `Minigame challenge must include lane pattern (got ${Array.isArray(nextChallenge?.pattern) ? nextChallenge.pattern.length : 0} steps). Restart the game server.`
        );
      }
      if (!Number.isInteger(nextChallenge?.laneCount)) {
        throw new Error('Minigame challenge must include laneCount. Restart the game server.');
      }
    }
    if (nextMinigameId === 'pixelPastRescue' && surfacePayload.spawned === true) {
      if (!Number.isInteger(nextChallenge?.winPickValue)) {
        throw new Error('Minigame challenge must include winPickValue. Restart the game server.');
      }
      if (!Number.isInteger(nextChallenge?.lossPickValue)) {
        throw new Error('Minigame challenge must include lossPickValue. Restart the game server.');
      }
      if (!Number.isInteger(nextChallenge?.maxLives)) {
        throw new Error('Minigame challenge must include maxLives. Restart the game server.');
      }
    }
    if (nextMinigameId === 'boatSharkCrossing' && surfacePayload.spawned === true) {
      if (!Number.isInteger(nextChallenge?.winPickValue)) {
        throw new Error('Minigame challenge must include winPickValue. Restart the game server.');
      }
      if (!Number.isInteger(nextChallenge?.lossPickValue)) {
        throw new Error('Minigame challenge must include lossPickValue. Restart the game server.');
      }
      if (!Number.isInteger(nextChallenge?.sharkSpawnIntervalMs)) {
        throw new Error('Minigame challenge must include sharkSpawnIntervalMs. Restart the game server.');
      }
      if (!Number.isInteger(nextChallenge?.sharksPerSpawn)) {
        throw new Error('Minigame challenge must include sharksPerSpawn. Restart the game server.');
      }
      if (typeof nextChallenge?.sharkSpeedMin !== 'number' || typeof nextChallenge?.sharkSpeedMax !== 'number') {
        throw new Error('Minigame challenge must include shark speeds. Restart the game server.');
      }
      if (!Number.isInteger(nextChallenge?.maxActiveSharks)) {
        throw new Error('Minigame challenge must include maxActiveSharks. Restart the game server.');
      }
      if (!Number.isInteger(nextChallenge?.maxLives)) {
        throw new Error('Minigame challenge must include maxLives. Restart the game server.');
      }
    }
    setSpawned(surfacePayload.spawned === true);
    setMarker(
      Array.isArray(surfacePayload.marker) && surfacePayload.marker.length === 2
        ? surfacePayload.marker
        : null
    );
    setMinigameId(nextMinigameId);
    setChallenge(nextChallenge);
    setOutcome(typeof surfacePayload.outcome === 'string' ? surfacePayload.outcome : null);
    setActiveMissionIndex(
      Number.isInteger(surfacePayload.activeMissionIndex) ? surfacePayload.activeMissionIndex : null
    );
  }, []);

  const resetLocal = useCallback(() => {
    setResolveBusy(false);
    setResolveError(null);
    setSpawned(false);
    setMarker(null);
    setMinigameId(null);
    setChallenge(null);
    setOutcome(null);
    setModalOpen(false);
    setCompleteBusy(false);
    setCompleteError(null);
    setCompleteOutcome(null);
    setCompleteWinReward(null);
    setCompleteWinRewards(null);
    setCompleteGambleResolution(null);
    setActiveMissionIndex(null);
    lastResolveKeyRef.current = null;
  }, []);

  useEffect(() => {
    if (activeRunId == null) {
      resetLocal();
      return;
    }
    if (lastResolveRunIdRef.current !== activeRunId) {
      lastResolveRunIdRef.current = activeRunId;
      lastResolveKeyRef.current = null;
    }
    if (sequencePhase !== 'arrival') {
      setModalOpen(false);
      return;
    }
    if (!isExploreUnknownMinigameEnabledForSurface(exploreSurface)) {
      setSpawned(false);
      setMarker(null);
      setMinigameId(null);
      setChallenge(null);
      setOutcome(null);
      setActiveMissionIndex(null);
      return;
    }
    if (
      nextAvailableMissionIndex == null ||
      !Number.isInteger(nextAvailableMissionIndex) ||
      nextAvailableMissionIndex < 1 ||
      !isGlobalMissionOnExploreUnknownMinigameSurface(exploreSurface, nextAvailableMissionIndex)
    ) {
      setSpawned(false);
      setMarker(null);
      setMinigameId(null);
      setChallenge(null);
      setOutcome(null);
      setActiveMissionIndex(null);
      return;
    }

    const missionIndex = Number(nextAvailableMissionIndex);
    if (!Number.isInteger(missionIndex) || missionIndex < 1) {
      return;
    }

    const resolveKey = `${activeRunId}:${exploreSurface}:${missionIndex}`;
    const needsFullBirdSequence =
      spawned &&
      minigameId === 'beyondBirdMemory' &&
      (!Array.isArray(challenge?.sequence) ||
        challenge.sequence.length !== EXPLORE_UNKNOWN_MINIGAME_BEYOND_BIRD_POP_COUNT);
    const needsSpottingObjectsFactionRefresh =
      spawned &&
      minigameId === 'spottingObjects' &&
      (!Array.isArray(challenge?.factionOptions) ||
        challenge.factionOptions.length !== EXPLORE_UNKNOWN_MINIGAME_SPOTTING_OBJECTS_FACTION_COUNT);
    if (lastResolveKeyRef.current === resolveKey && !needsFullBirdSequence && !needsSpottingObjectsFactionRefresh) {
      return;
    }
    lastResolveKeyRef.current = resolveKey;

    let cancelled = false;
    (async () => {
      setResolveBusy(true);
      setResolveError(null);
      try {
        const token = await ensureValidToken();
        const response = await fetch(`${API_URL}/explore-unknown/minigame/resolve-surface`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            runId: activeRunId,
            surface: exploreSurface,
            missionIndex,
          }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.error || `Minigame resolve failed (${response.status})`);
        }
        if (cancelled) return;
        applyMinigameSurfacePayload(data.surface);
      } catch (err) {
        if (!cancelled) {
          lastResolveKeyRef.current = null;
          setResolveError(err instanceof Error ? err.message : String(err));
        }
      } finally {
        if (!cancelled) {
          setResolveBusy(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    activeRunId,
    exploreSurface,
    sequencePhase,
    ensureValidToken,
    resetLocal,
    applyMinigameSurfacePayload,
    nextAvailableMissionIndex,
    activeRunId,
  ]);

  const handleDevStartBirdMinigameTest = useCallback(async () => {
    if (activeRunId == null) {
      throw new Error('Dev minigame test: active run is required');
    }
    if (!isExploreUnknownMinigameEnabledForSurface(exploreSurface)) {
      throw new Error(`Dev minigame test: surface ${exploreSurface} has no enabled minigame`);
    }
    setDevMinigameTestBusy(true);
    setDevMinigameTestError(null);
    setCompleteError(null);
    try {
      const token = await ensureValidToken();
      const response = await fetch(`${API_URL}/explore-unknown/dev-force-minigame`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ runId: activeRunId, surface: exploreSurface }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || `Dev force minigame failed (${response.status})`);
      }
      applyMinigameSurfacePayload(data.surface);
      lastResolveKeyRef.current = `${activeRunId}:${exploreSurface}`;
      setModalOpen(true);
    } catch (err) {
      setDevMinigameTestError(err instanceof Error ? err.message : String(err));
    } finally {
      setDevMinigameTestBusy(false);
    }
  }, [activeRunId, exploreSurface, ensureValidToken, applyMinigameSurfacePayload]);

  const showMinigameMarker =
    sequencePhase === 'arrival' &&
    activeRunId != null &&
    spawned &&
    outcome === 'pending' &&
    activeMissionIndex != null &&
    activeMissionIndex === Number(nextAvailableMissionIndex) &&
    Array.isArray(marker) &&
    marker.length === 2 &&
    minigameId != null;

  const minigameMarkerInteractive = showMinigameMarker && !resolveBusy && !completeBusy;

  const handleMinigameMarkerActivate = useCallback(() => {
    if (!minigameMarkerInteractive) return;
    setCompleteError(null);
    setModalOpen(true);
  }, [minigameMarkerInteractive]);

  const handleMinigameModalDismiss = useCallback(() => {
    if (completeBusy) return;
    setModalOpen(false);
    setCompleteError(null);
    setCompleteOutcome(null);
    setCompleteWinReward(null);
    setCompleteWinRewards(null);
  }, [completeBusy]);

  const handleMinigameComplete = useCallback(
    async (pickedTreeIndex, completeOpts = {}) => {
      if (activeRunId == null || completeBusy) return;
      if (!Number.isInteger(pickedTreeIndex) || pickedTreeIndex < 0) {
        throw new Error(`handleMinigameComplete: invalid pickedTreeIndex (${String(pickedTreeIndex)})`);
      }
      setCompleteBusy(true);
      setCompleteError(null);
      setCompleteOutcome(null);
      setCompleteWinReward(null);
      setCompleteWinRewards(null);
      setCompleteGambleResolution(null);
      try {
        const token = await ensureValidToken();
        const body = {
          runId: activeRunId,
          surface: exploreSurface,
          pickedTreeIndex,
        };
        if (Array.isArray(completeOpts.matchedPairs) && completeOpts.matchedPairs.length > 0) {
          body.matchedPairs = completeOpts.matchedPairs.map((pair) => {
            if (
              pair == null ||
              !Number.isInteger(pair.firstCardIndex) ||
              !Number.isInteger(pair.secondCardIndex)
            ) {
              throw new Error('handleMinigameComplete: invalid matchedPairs entry');
            }
            return {
              firstCardIndex: pair.firstCardIndex,
              secondCardIndex: pair.secondCardIndex,
            };
          });
        }
        if (Array.isArray(completeOpts.poppedBalloonIndices) && completeOpts.poppedBalloonIndices.length > 0) {
          body.poppedBalloonIndices = completeOpts.poppedBalloonIndices.map((idx) => {
            if (!Number.isInteger(idx)) {
              throw new Error('handleMinigameComplete: invalid poppedBalloonIndices entry');
            }
            return idx;
          });
        }
        if (Array.isArray(completeOpts.gambleDrawIndices) && completeOpts.gambleDrawIndices.length > 0) {
          body.gambleDrawIndices = completeOpts.gambleDrawIndices.map((idx) => {
            if (!Number.isInteger(idx)) {
              throw new Error('handleMinigameComplete: invalid gambleDrawIndices entry');
            }
            return idx;
          });
        }
        if (completeOpts.gambleBust === true) {
          body.gambleBust = true;
        }
        if (Array.isArray(completeOpts.pickedLaneIndices) && completeOpts.pickedLaneIndices.length > 0) {
          body.pickedLaneIndices = completeOpts.pickedLaneIndices.map((idx) => {
            if (!Number.isInteger(idx)) {
              throw new Error('handleMinigameComplete: invalid pickedLaneIndices entry');
            }
            return idx;
          });
        }
        const response = await fetch(`${API_URL}/explore-unknown/minigame/complete`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.error || `Minigame complete failed (${response.status})`);
        }
        if (data.darknessPercent !== undefined && data.darknessPercent !== null) {
          onDarknessPercentUpdated(Number(data.darknessPercent));
        }
        const surfacePayload = data.surface;
        const won =
          data.success === true ||
          (surfacePayload && typeof surfacePayload === 'object' && surfacePayload.outcome === 'won');
        const nextOutcome = won ? 'won' : 'lost';
        setOutcome(
          surfacePayload && typeof surfacePayload.outcome === 'string'
            ? surfacePayload.outcome
            : nextOutcome
        );
        const payloadWinRewards = Array.isArray(data.winRewards)
          ? data.winRewards.filter((r) => r && typeof r === 'object')
          : null;
        if (won && typeof onRunStateSyncedAfterComplete === 'function') {
          await onRunStateSyncedAfterComplete({
            winReward: data.winReward && typeof data.winReward === 'object' ? data.winReward : null,
            winRewards: payloadWinRewards,
          });
        }
        setCompleteOutcome(nextOutcome);
        if (data.gambleResolution && typeof data.gambleResolution === 'object') {
          setCompleteGambleResolution(data.gambleResolution);
        }
        if (won && payloadWinRewards && payloadWinRewards.length > 0) {
          setCompleteWinRewards(payloadWinRewards);
          setCompleteWinReward(payloadWinRewards[payloadWinRewards.length - 1]);
        } else if (won && data.winReward && typeof data.winReward === 'object') {
          setCompleteWinReward(data.winReward);
        }
      } catch (err) {
        setCompleteError(err instanceof Error ? err.message : String(err));
      } finally {
        setCompleteBusy(false);
      }
    },
    [
      activeRunId,
      completeBusy,
      ensureValidToken,
      exploreSurface,
      onDarknessPercentUpdated,
      onRunStateSyncedAfterComplete,
      applyMinigameSurfacePayload,
    ]
  );

  return {
    resolveBusy,
    resolveError,
    showMinigameMarker,
    minigameMarkerPoint: showMinigameMarker ? marker : null,
    minigameMarkerInteractive,
    minigameId,
    minigameChallenge: challenge,
    minigameModalOpen: modalOpen,
    minigameCompleteBusy: completeBusy,
    minigameCompleteError: completeError,
    minigameCompleteOutcome: completeOutcome,
    minigameCompleteWinReward: completeWinReward,
    minigameCompleteWinRewards: completeWinRewards,
    minigameCompleteGambleResolution: completeGambleResolution,
    handleMinigameMarkerActivate,
    handleMinigameModalDismiss,
    handleMinigameComplete,
    handleDevStartBirdMinigameTest,
    devMinigameTestBusy,
    devMinigameTestError,
    resetMinigameSurface: resetLocal,
  };
}
